import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import swal from 'sweetalert';
import { getMySharedRentals, markCompleteSharedRental } from '../../api/sharedRentalsApi';
import VendorOrdersCalendar from './VendorOrdersCalendar';
import { VendorPayoutLegendVendor } from '../admin/AdminStatusLegend';
import { vendorPayoutStatus, vendorTripMonthKey, formatVendorShare } from '../../utils/vendorPayoutStatus';
import '../admin/BookingCalendar.scss';

export default function PartnerSharedRentalsList() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchRentals = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getMySharedRentals({ per_page: 500, sort_by: 'date', sort_order: 'desc' });
      const raw = res.data?.shared_rentals || res.data || [];
      setRentals(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.warn('PartnerSharedRentalsList fetch error', e);
      setError(true);
      setRentals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRentals(); }, [fetchRentals]);

  const handleMarkComplete = (r) => {
    if (!r.idx) return;
    setMarkingId(r.idx);
    markCompleteSharedRental(r.idx)
      .then(() => { swal('Done', 'Marked complete. Admin will record your payment.', 'success'); fetchRentals(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
      .finally(() => setMarkingId(null));
  };

  return (
    <div className="container py-4">
      <Link to="/partner" className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
      <h2 className="mb-3">Shared Rentals</h2>
      <VendorPayoutLegendVendor />
      <p className="text-muted small mb-3">Click a month → mark complete when the rental is done.</p>

      {error && (
        <div className="alert alert-warning">
          Could not load rentals. <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchRentals}>Retry</button>
        </div>
      )}

      {!error && (
        <VendorOrdersCalendar
          items={rentals}
          getMonthKey={vendorTripMonthKey}
          loading={loading}
          emptyMessage="No shared rentals assigned yet."
          renderTable={(monthItems) => (
            <div className="table-responsive">
              <table className="table table-sm table-hover booking-cal__table">
                <thead>
                  <tr>
                    <th>Rental</th>
                    <th>Date</th>
                    <th>People</th>
                    <th>Your amount</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {monthItems.map((r) => {
                    const status = vendorPayoutStatus({ ...r, vendor_amount: r.vendor_amount ?? r.vendor_payout }, { audience: 'vendor' });
                    return (
                      <tr key={r.idx || r.id}>
                        <td><strong>{r.name || '—'}</strong></td>
                        <td>{r.start_date ? moment(r.start_date).format('D MMM YYYY') : '—'}</td>
                        <td>{r.paid_people_count ?? '—'} paid · min {r.min_group_size ?? '—'}</td>
                        <td>{formatVendorShare(r)}</td>
                        <td>
                          <span className={`badge badge-${status.badge}`}>{status.label}</span>
                          {status.hint && <div className="small text-muted">{status.hint}</div>}
                        </td>
                        <td className="text-right">
                          {!r.vendor_completed_at && (
                            <button type="button" className="btn btn-sm btn-success" onClick={() => handleMarkComplete(r)} disabled={markingId === r.idx}>
                              {markingId === r.idx ? '…' : 'Mark complete'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        />
      )}
    </div>
  );
}
